import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/shared/ui/Toast';
import { agentsApi } from '../api/agentsApi';
import { agentsKeys, AGENT_POLL_MS } from '../queryKeys';

// A task's output version history. Only fetched while the panel is open (`enabled`), mirroring
// useTaskThread — including its 3s poll, so when a chat reply makes Sam revise the draft the new
// version appears in the bar on its own (the incoming reply doesn't invalidate this query).
// Restoring an older version invalidates both the versions list (so `current` moves) and the task
// list (payload.proposals/summary mirror the newly-current version).
export function useTaskVersions(taskId: string, locationId: string, enabled: boolean) {
  const qc = useQueryClient();
  const toast = useToast();
  const versionsQuery = useQuery({
    queryKey: agentsKeys.versions(locationId, taskId),
    queryFn: () => agentsApi.getVersions(locationId, taskId),
    enabled: enabled && !!locationId,
    refetchInterval: enabled ? AGENT_POLL_MS : false,
  });
  const restore = useMutation({
    mutationFn: (n: number) => agentsApi.restoreVersion(locationId, taskId, n),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: agentsKeys.versions(locationId, taskId) });
      qc.invalidateQueries({ queryKey: agentsKeys.tasks(locationId) });
    },
    onError: (err: Error) => toast.error(err.message ?? 'Failed to restore version'),
  });
  return {
    versions: versionsQuery.data?.versions ?? [],
    current: versionsQuery.data?.current ?? 0,
    isLoading: versionsQuery.isLoading,
    restore: restore.mutate,
    restoring: restore.isPending,
  };
}
